/**
 * Demo scheduling (GH-901): the sales pipeline's calendar — a staff-defined weekly availability
 * template, the open slots it produces, and the appointments booked against it.
 *
 *   GET   /v1/public/demo-availability             public, unauthenticated — open slots only
 *   POST  /v1/public/demo-bookings                 public, unauthenticated, rate-limited
 *   GET   /v1/admin/availability-windows            platform staff
 *   PUT   /v1/admin/availability-windows            platform staff, audited — replaces the template
 *   GET   /v1/admin/demo-appointments               platform staff — the calendar view's data
 *   PATCH /v1/admin/demo-appointments/:id           platform staff, audited — confirm/cancel/reschedule
 *
 * Replaces GH-897/898's marketing-site calendar, which computed its own 3-business-day window
 * client-side with nothing server-side checking whether a slot was actually free.
 */
import { z } from "zod";
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
const weekdaySchema = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
]);
const CLOCK_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
/** "HH:MM", 24-hour, no seconds — what a plain `<input type="time">` already produces. */
const clockTimeSchema = z.string().regex(CLOCK_TIME_PATTERN, "Expected HH:MM.");
/** One staff-defined recurring window, e.g. "Tuesdays 10:00-12:00, America/New_York". */
export const availabilityWindowSchema = z.object({
    id: z.string().uuid(),
    weekday: weekdaySchema,
    startsAt: clockTimeSchema,
    endsAt: clockTimeSchema,
    timezone: z.string().min(1).max(100),
});
export const availabilityWindowListResponseSchema = z.object({
    windows: z.array(availabilityWindowSchema),
});
/** Staff replaces the whole weekly template in one call — see `availability-windows.ts`'s
 *  `replaceAll` for why this is a set operation, not row-by-row edits. */
export const setAvailabilityWindowsRequestSchema = z.object({
    windows: z
        .array(z
        .object({
        weekday: weekdaySchema,
        startsAt: clockTimeSchema,
        endsAt: clockTimeSchema,
        timezone: z.string().min(1).max(100),
    })
        .refine((window) => window.startsAt < window.endsAt, {
        message: "A window must end after it starts.",
        path: ["endsAt"],
    }))
        .max(50),
});
export const DEMO_APPOINTMENT_STATUSES = ["tentative", "confirmed", "cancelled"];
export const demoAppointmentStatusSchema = z.enum(DEMO_APPOINTMENT_STATUSES);
/** One booked slot, as the console's calendar renders it — who it is with, not just when. */
export const demoAppointmentRowSchema = z.object({
    id: z.string().uuid(),
    salesLeadId: z.string().uuid(),
    leadName: z.string(),
    leadOrganizationName: z.string(),
    startsAt: z.string(),
    endsAt: z.string(),
    status: demoAppointmentStatusSchema,
    rescheduleOf: z.string().uuid().nullable(),
    notes: z.string().nullable(),
    /** The lead's assignment (GH-904), carried onto the appointment row so the console calendar
     *  can show it without a second lookup. Null when the lead has no assigned staff member. */
    assignedStaffUserId: z.string().uuid().nullable(),
    assignedStaffUserName: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const demoAppointmentListResponseSchema = z.object({
    appointments: z.array(demoAppointmentRowSchema),
});
/** `from`/`to` bound every range query here — ISO instants, half-open `[from, to)`. */
export const dateRangeQuerySchema = z.object({
    from: z.string().datetime(),
    to: z.string().datetime(),
});
/** A staff decision on an existing appointment. Confirm or cancel it in place, or propose a new
 *  time — which cancels this one and books a new tentative appointment linked to it, never
 *  reopens or moves this row (CLAUDE.md: reversal and replacement, not mutation in place). */
export const demoAppointmentDecisionRequestSchema = z.discriminatedUnion("action", [
    z.object({ action: z.literal("confirm") }),
    z.object({ action: z.literal("cancel") }),
    z.object({
        action: z.literal("reschedule"),
        startsAt: z.string().datetime(),
        endsAt: z.string().datetime(),
    }),
    z.object({ action: z.literal("set_notes"), notes: z.string().trim().max(2000).nullable() }),
]);
/** What the marketing site's calendar fetches instead of inventing its own weekday math. */
export const openSlotSchema = z.object({
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
});
export const demoAvailabilityResponseSchema = z.object({
    slots: z.array(openSlotSchema),
});
/** The marketing site's real booking call — replaces folding "Preferred time" text into
 *  `sales_leads.message` (GH-897). Creates the lead and the appointment together. */
export const demoBookingCreateRequestSchema = z.object({
    name: z.string().trim().min(1).max(200),
    email: z.string().trim().min(3).max(320).email(),
    organizationName: z.string().trim().min(1).max(200),
    phone: z.string().trim().min(1).max(32).optional(),
    message: z.string().trim().min(1).max(2000).optional(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
});
export const demoBookingCreateResponseSchema = z.object({
    leadId: z.string().uuid(),
    appointmentId: z.string().uuid(),
    status: z.literal("tentative"),
});
//# sourceMappingURL=demo-scheduling.js.map